import { FinishedTournaments, FinishedTournament } from '@/model/ranking/FinishedTournament'
import { sliceAndSortRankings, formatWinnings } from '@/store/modules/ranking/index'
import { SortOrder, SortFilter, Sorting } from '@/model/ranking/Sorting'
import { State } from '@/store/state'
import { formatMoneyAmount } from '@/lib/formattingUtils'
import { ERROR_CODES } from '@/model/ErrorCodes'

export interface ZoneHistoryState {
  rankings: Array<FinishedTournament>
  sorting: Sorting
}

const state: ZoneHistoryState = {
  rankings: [],
  sorting: {
    order: SortOrder.ASCENDING,
    filter: SortFilter.Tournament,
  },
}

export const getters = {
  rankings:
    (state: ZoneHistoryState, getters: any, rootState: State) =>
    (from?: number, to?: number): Array<FinishedTournament> => {
      const rankings = sliceAndSortRankings(
        [...state.rankings],
        from,
        to,
        sortHistory(state.sorting)
      ) as Array<FinishedTournament>
      return formatWinnings(rankings, rootState.user.currency) as Array<FinishedTournament>
    },
  accumulatedWinnings:
    (state: ZoneHistoryState, getters: any, rootState: State) =>
    (from?: number, to?: number): string =>
      formatMoneyAmount(
        [...state.rankings]
          .slice(from, to)
          .reduce((acc, ranking) => acc + parseFloat(ranking.totalWin), 0)
          .toString(),
        rootState.user.currency
      ),
}

const actions = {
  fetch({ commit, getters }: any, zoneId: string) {
    // TODO add cache to not load always the same data
    return new Promise((res: (value?: unknown) => void, rej: (reason?: unknown) => void) => {
      fetch(`${process.env.VUE_APP_REST_URL}ranking/zoneHistory/${zoneId}`, {
        credentials: 'same-origin',
        cache: 'no-cache',
      })
        .then((res: Response) => {
          if (res.status >= 400) {
            commit('error', { id: 'ranking/zoneHistory/fetch', responseStatus: res.status }, { root: true })
            throw { errorCode: res.status === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.UNKNOWN }
          }
          return res.json()
        })
        .then((data: FinishedTournaments) => {
          commit('rankings', data.finishedTournaments)
          res()
        })
        .catch((err) => {
          rej(err)
        })
    })
  },
  /**
   * TODO: why not using the mutation directly?
   * @param param0
   * @param sorting
   */
  toggleSortingForFilter({ commit, state }: any, sorting: Sorting) {
    commit('sortingFilter', sorting.filter)
    commit('sortingOrder', sorting.order)
  },
}

const mutations = {
  rankings(state: ZoneHistoryState, list: Array<FinishedTournament>) {
    state.rankings = list
  },
  sortingOrder(state: ZoneHistoryState, order: SortOrder) {
    state.sorting.order = order
  },
  sortingFilter(state: ZoneHistoryState, filter: SortFilter) {
    state.sorting.filter = filter
  },
}

const sortHistory = (sorting: Sorting) => (a: FinishedTournament, b: FinishedTournament) => {
  const sort = createSortFn(sorting.order)
  switch (sorting.filter) {
    case SortFilter.Date:
      return sort(a.competitionEnd, b.competitionEnd)
    case SortFilter.Rank:
      return sort(a.tournamentRank, b.tournamentRank)
    case SortFilter.Score:
      return sort(a.tournamentScore, b.tournamentScore)
    case SortFilter.TotalWin:
      return sort(parseFloat(a.totalWin), parseFloat(b.totalWin))
    case SortFilter.Tournament:
      return sort(a.tournamentId, b.tournamentId)
    default:
      return sort(a.tournamentId, b.tournamentId)
  }
}

const createSortFn = (order: SortOrder) => (a: any, b: any) => {
  switch (order) {
    case SortOrder.ASCENDING:
      return b < a ? 1 : -1
    case SortOrder.DESCENDING:
      return b > a ? 1 : -1
    default:
      return b > a ? 1 : -1
  }
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}
